import { useCallback } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import type { SurveySection } from '../types';
import { createBlankQuestion, duplicateSection } from '../utils/transform';
import { applyVisibilityCleanup } from '../utils/validation';

const FIELD_ARRAY_NAME = 'surveyForm.surveyParts';
// `useFieldArray` writes its reconciliation id under this property name instead of `id`
// so it doesn't collide with the SurveySection's own `id` field.
const RHF_KEY_NAME = '_rhfId';

interface UseSectionsFormArrayReturn {
	/** Live list of sections from form state. */
	sections: SurveySection[];
	/** Append a new empty section (empty-string title/description, no questions). */
	appendEmptySection: () => void;
	/** Remove a section by its index in `surveyForm.surveyParts`. */
	removeSection: (index: number) => void;
	/** Move a section from one index to another. */
	moveSection: (from: number, to: number) => void;
	/** Replace the whole sections array with a new order (full-permutation reorder). */
	reorderSections: (orderedParts: SurveySection[]) => void;
	/** Duplicate a section in place — fresh ids, internal-ref rewrite (see `duplicateSection`). */
	duplicateSectionAt: (index: number) => void;
}

/**
 * Hook wrapping `react-hook-form`'s `useFieldArray` for the V2 sections list at
 * `surveyForm.surveyParts`. Exposes the section-level operations consumed by
 * `QuestionsWithSections` and `SectionCard`. Question-level mutation happens through
 * the existing V1 `FieldList` component (parametrized with the per-section field path).
 */
export function useSectionsFormArray(): UseSectionsFormArrayReturn {
	const { control, getValues, setValue } = useFormContext();
	const fieldArray = useFieldArray({ control, name: FIELD_ARRAY_NAME, keyName: RHF_KEY_NAME });
	// rhf's reconciliation id now lives at `_rhfId`, so `fields[i].id` is the original
	// SurveySection id from form values.
	const sections = fieldArray.fields as unknown as SurveySection[];

	const appendEmptySection = useCallback(() => {
		// Seed with one blank question so the user has somewhere to type immediately
		// and so the BE's "at least one question per section" invariant is satisfiable
		// without an extra click. Shared seed helper keeps id ↔ fieldId in lockstep
		// so visibility eligibility resolves on brand-new questions.
		fieldArray.append({
			_type: 'SurveySection',
			id: uuid(),
			title: '',
			description: '',
			questions: [createBlankQuestion()],
		} as unknown as SurveySection);
	}, [fieldArray]);

	const removeSection = useCallback(
		(index: number) => {
			// A V2 survey must always keep at least one section; dropping to zero would
			// collapse the editor back to the V1 AdditionalQuestions view. Guard here so
			// every code path (hook + UI) is covered even if the UI guard is bypassed.
			if (fieldArray.fields.length <= 1) return;
			fieldArray.remove(index);
			// Removing a section may invalidate visibility rules on later sections /
			// questions that depended on questions inside the removed section.
			applyVisibilityCleanup(getValues, setValue);
		},
		[fieldArray, getValues, setValue]
	);

	const moveSection = useCallback(
		(from: number, to: number) => {
			fieldArray.move(from, to);
			// Reordering can break the "trigger source must precede dependent" invariant.
			applyVisibilityCleanup(getValues, setValue);
		},
		[fieldArray, getValues, setValue]
	);

	const reorderSections = useCallback(
		(orderedParts: SurveySection[]) => {
			fieldArray.replace(orderedParts as unknown as SurveySection[]);
			// A full reorder can break the "trigger source must precede dependent" invariant; sweep.
			applyVisibilityCleanup(getValues, setValue);
		},
		[fieldArray, getValues, setValue]
	);

	const duplicateSectionAt = useCallback(
		(index: number) => {
			// Read the LIVE section from form state, not `fieldArray.fields[index]`.
			// `useFieldArray.fields` is a snapshot that RHF only refreshes on
			// array-level ops; sub-path setValue mutations (e.g. "+ Add Question",
			// label edits, type changes, option edits) bypass it. Cloning from the
			// snapshot would lose every edit since the last structural op.
			const liveParts = getValues(FIELD_ARRAY_NAME) as SurveySection[] | undefined;
			const source = liveParts?.[index];
			if (!source) return;
			fieldArray.insert(index + 1, duplicateSection(source) as SurveySection);
			// `duplicateSection` rewrites internal refs; an additional sweep keeps
			// any external refs that pointed at the (now-shifted) original consistent.
			applyVisibilityCleanup(getValues, setValue);
		},
		[fieldArray, getValues, setValue]
	);

	return { sections, appendEmptySection, removeSection, moveSection, reorderSections, duplicateSectionAt };
}
